// @ts-nocheck

import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  Box,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  MenuItem,
  Select,
  Typography,
  useTheme,
  Tooltip,
  styled,
} from '@mui/material';
import { SolyticsButton } from '../../../package/components';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Handle, Position, useViewport } from 'reactflow';
import { upperCase } from 'lodash';
import PropTypes from 'prop-types';
import UseCaseIcon from '@mui/icons-material/Diversity2';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { ReactComponent as ModelIcon } from '../../../assets/images/ModelDetails/ModelLogo.svg';
import { ReactComponent as ArtifactIcon } from '../../../assets/Icons/ArtifactsIcon.svg';
import { ReactComponent as AttributeIcon } from '../../../assets/Icons/Global Attributes.svg';
import { ReactComponent as ModelRequestIcon } from '../../../assets/Icons/ModelRequestIcon.svg';
import { ReactComponent as QueryIcon } from '../../../assets/Icons/Query 3.svg';
import {
  getEntityTypeLabel,
  getEntityTypeKey,
  getEntityTypes,
  getLabelPathFromLabelConfig,
} from '../../../utils/Utils.jsx';
import AlertHealthIndicator from '../ModelInventoryAndAssociation/AlertHealthIndicator';
import { ENTITY_TYPE } from '../../../const/CommonConst.js';
import { DEFAULT_ROUTE_CONFIG } from '../../../const/defaultRoutesConfig.js';
import { useContextMenu } from 'react-contexify';
import ReactMuiTableListViewV8 from '../ReactMuiTableListViewV8';

const DotIndicator = styled(Box)(() => ({
  width: '13.6px',
  height: '13.6px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  cursor: 'default',
}));

export const EntityIcon = ({
  entity_type,
  request_type,
  size = 21,
  wrapperSize = 30,
}) => {
  const theme = useTheme();
  const { color, backgroundColor, Icon } = useMemo(() => {
    const type = getEntityTypes(entity_type, request_type);
    switch (type) {
      case ENTITY_TYPE.ModelInventory:
        return {
          color: theme?.palette?.landscape?.model,
          backgroundColor: theme?.palette?.landscape?.bg?.model,
          Icon: ModelIcon,
        };
      case ENTITY_TYPE.ModelAssociation:
        return {
          color: theme?.palette?.landscape?.artifact,
          backgroundColor: theme?.palette?.landscape?.bg?.artifact,
          Icon: ArtifactIcon,
        };
      case ENTITY_TYPE.UseCase:
        return {
          color: theme?.palette?.landscape?.useCase,
          backgroundColor: theme?.palette?.landscape?.bg?.useCase,
          Icon: UseCaseIcon,
        };
      case ENTITY_TYPE.ModelRequest:
        return {
          color: theme?.palette?.landscape?.assessment,
          backgroundColor: theme?.palette?.landscape?.bg?.assessment,
          Icon: ModelRequestIcon,
        };
      case ENTITY_TYPE.Query:
        return {
          color: theme?.palette?.landscape?.query,
          backgroundColor: theme?.palette?.landscape?.bg?.query,
          Icon: QueryIcon,
        };
      case 'AccessRequest':
        return {
          color: theme?.palette?.primary?.main,
          backgroundColor: theme?.palette?.landscape?.bg?.accessRequest,
          Icon: VpnKeyIcon,
        };
      default:
        return {
          color: '#AEC9F6',
          backgroundColor: '#AEC9F6',
          Icon: AttributeIcon,
        };
    }
  }, [entity_type, request_type, theme]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '100%',
        background: backgroundColor,
        width: `${wrapperSize}px`,
        height: `${wrapperSize}px`,
      }}
    >
      <Icon
        style={{
          width: `${size}px`,
          height: `${size}px`,
          color,
        }}
      />
    </Box>
  );
};
EntityIcon.propTypes = {
  entity_type: PropTypes.string.isRequired,
  request_type: PropTypes.string,
  size: PropTypes.number,
  wrapperSize: PropTypes.number,
};

const TitleComponent = ({ data, theme, entityName }) => {
  // Returns the badge background and text color tokens for the given alert severity flag.
  const getBadgeColors = useCallback(
    (flag) => {
      switch (flag) {
        case 'Low':
          return {
            bg: theme?.palette?.examinationResults?.severityLowBg,
            color: theme?.palette?.examinationResults?.severityLow,
          };
        case 'Medium':
          return {
            bg: theme?.palette?.examinationResults?.severityMediumBg,
            color: theme?.palette?.examinationResults?.severityMedium,
          };
        case 'High':
          return {
            bg: theme?.palette?.examinationResults?.severityHighBg,
            color: theme?.palette?.examinationResults?.severityHigh,
          };
        case 'Critical':
          return {
            bg: theme?.palette?.examinationResults?.severityCriticalBg,
            color: theme?.palette?.examinationResults?.severityCritical,
          };
        default:
          return {
            bg: theme?.palette?.grey?.[200],
            color: theme?.palette?.text?.primary,
          };
      }
    },
    [theme]
  );

  const renderRow = useCallback(
    (label, value, titleMaxWidth = '150px') => (
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography
          variant="body5"
          sx={{
            paddingRight: '8px',
            color: theme?.palette?.landscape?.tooltipLabelColor,
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body4"
          title={value || ''}
          sx={{
            textAlign: 'right',
            maxWidth: titleMaxWidth,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: theme?.palette?.grey?.T80,
          }}
        >
          {value || ' '}
        </Typography>
      </Box>
    ),
    [theme]
  );

  return (
    <Card
      className="landscape-title-card"
      sx={{
        borderColor: theme?.palette?.other?.borderColor,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <EntityIcon
            entity_type={data?.entity_type}
            request_type={data?.request_type}
          />
          <Typography
            variant="body4"
            title={data?.unique_id ?? data?.entity_id}
            sx={{
              color: theme?.palette?.grey?.T80,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {data?.unique_id ?? data?.entity_id}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            backgroundColor: getBadgeColors(data?.Flag).bg,
            padding: '2px 8px',
            borderRadius: '16px',
          }}
        >
          <AlertHealthIndicator flag={data?.Flag} iconSize="8px" />
          <Typography
            variant="body5"
            sx={{ color: getBadgeColors(data?.Flag).color }}
          >
            {data?.Flag}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {renderRow('Name', entityName)}
        {renderRow('Status', data?.status)}
        {renderRow('Assigned To', data?.assignedUser, '200px')}
        {renderRow('Template', data?.template_type)}
        {renderRow('Workflow', data?.workflowLabel)}
      </Box>
    </Card>
  );
};
TitleComponent.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  theme: PropTypes.instanceOf(Object).isRequired,
  entityName: PropTypes.string.isRequired,
};

export const SingleEntityNode = memo((props) => {
  const data = props?.data?.properties;
  const theme = useTheme();
  const entityName = props?.data?.label;
  const entityId = data?.unique_id;

  const { show: showContextMenu } = useContextMenu();
  const handleContextMenu = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      showContextMenu({
        id: 'node-rightClick',
        event: e,
        props,
      });
    },
    [props, showContextMenu]
  );

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            p: 0,
          },
        },
      }}
      title={
        <TitleComponent data={data} theme={theme} entityName={entityName} />
      }
    >
      <Card
        onContextMenu={handleContextMenu}
        className="landscape-entity-node"
        sx={{
          position: 'relative',
          borderColor: theme?.palette?.primary?.main,
          ...(props?.type === 'main_entity_node'
            ? { backgroundColor: theme?.palette?.landscape?.mainNode }
            : { backgroundColor: theme?.palette?.landscape?.bg?.main }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <EntityIcon
            entity_type={data?.entity_type}
            request_type={data?.request_type}
            {...(props?.type === 'main_entity_node'
              ? { size: 14, wrapperSize: 20 }
              : {})}
          />
          <Typography
            variant="body3"
            sx={{
              color: theme?.palette?.text?.secondary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {entityId}
          </Typography>
        </Box>
        <Handle
          type="source"
          position={Position.Right}
          id="source-uni"
          isConnectable={true}
          style={{
            top: '30%',
            opacity: 0,
          }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="source-bi"
          isConnectable={true}
          style={{
            top: '70%',
            opacity: 0,
          }}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="b"
          isConnectable={true}
          style={{
            top: '50%',
            opacity: 0,
          }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="target-uni"
          isConnectable={true}
          style={{
            top: '30%',
            opacity: 0,
          }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="target-bi"
          isConnectable={true}
          style={{
            top: '70%',
            opacity: 0,
          }}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="b"
          isConnectable={true}
          style={{
            top: '50%',
            opacity: 0,
          }}
        />
      </Card>
    </Tooltip>
  );
});
SingleEntityNode.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  type: PropTypes.string,
};

// this component will handle cluster_node node type

/**
 * EntityMenuItem - a single item inside the entity Select dropdown.
 * Extracted to avoid arrow functions in JSX event handler props.
 */
const EntityMenuItem = memo(
  ({ entity, uniqueId, count, showContextMenu, theme, zoom = 1, ...rest }) => {
    const handleContextMenu = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        showContextMenu({ id: 'node-rightClick', event: e, props: entity });
      },
      [entity, showContextMenu]
    );

    return (
      <MenuItem
        value={entity.id}
        onContextMenu={handleContextMenu}
        sx={{
          py: `${10 * zoom}px`,
          px: `${12 * zoom}px`,
          minHeight: 'auto',
          height: 'auto',
          borderBottom: `1px solid ${theme?.palette?.other?.borderColor}`,
          '&:first-of-type': {
            borderTop: `1px solid ${theme?.palette?.other?.borderColor}`,
          },
          '&:hover': {
            backgroundColor: theme?.palette?.landscape?.entityMenuItemHover,
          },
          '&.Mui-selected': {
            backgroundColor: theme?.palette?.landscape?.entityMenuItemHover,
          },
          '&.Mui-selected:hover': {
            backgroundColor: theme?.palette?.landscape?.entityMenuItemHover,
          },
        }}
        {...rest}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
            gap: `${8 * zoom}px`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: `${8 * zoom}px`,
              overflow: 'hidden',
              minWidth: 0,
              flex: 1,
            }}
          >
            <EntityIcon
              entity_type={entity?.entity_type}
              request_type={entity?.request_type || entity?.type}
              size={Math.round(13 * zoom)}
              wrapperSize={Math.round(20 * zoom)}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: `${4 * zoom}px`,
                overflow: 'hidden',
                minWidth: 0,
                flex: 1,
              }}
            >
              <Typography
                variant="body3"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: theme?.palette?.text?.primary,
                  fontSize: `${13 * zoom}px`,
                  lineHeight: `${20 * zoom}px`,
                }}
              >
                {uniqueId}
              </Typography>
              <Tooltip
                slotProps={{
                  tooltip: {
                    sx: { p: 0 },
                  },
                }}
                title={
                  <TitleComponent
                    data={entity?.data?.properties}
                    theme={theme}
                    entityName={
                      entity?.data?.label || entity?.data?.properties?.unique_id
                    }
                  />
                }
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: `${16 * zoom}px`,
                    color: theme?.palette?.text?.secondary,
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            </Box>
          </Box>
          {count !== undefined && count !== null && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: `${8 * zoom}px`,
                flexShrink: 0,
              }}
            >
              <DescriptionOutlinedIcon
                sx={{
                  fontSize: `${16 * zoom}px`,
                  color: theme?.palette?.landscape?.iconMuted,
                }}
              />
              <Typography
                variant="body3"
                sx={{
                  color: theme?.palette?.text?.primary,
                  fontSize: `${13 * zoom}px`,
                  lineHeight: `${20 * zoom}px`,
                }}
              >
                {count}
              </Typography>
            </Box>
          )}
        </Box>
      </MenuItem>
    );
  }
);
EntityMenuItem.propTypes = {
  entity: PropTypes.instanceOf(Object).isRequired,
  uniqueId: PropTypes.string,
  count: PropTypes.number,
  showContextMenu: PropTypes.func.isRequired,
  theme: PropTypes.instanceOf(Object).isRequired,
  zoom: PropTypes.number,
};

// Derives a deterministic display color from a node id string using a simple hash against a colors array.
const getColorFromId = (id, colors) => {
  let hash = 0;
  const str = String(id || '');
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const viewAllRowShape = PropTypes.shape({
  original: PropTypes.shape({
    name: PropTypes.string,
    template: PropTypes.string,
    status: PropTypes.string,
    workflow: PropTypes.string,
    createdAt: PropTypes.string,
  }),
});

// Renders the entity unique_id in the Name column of the "View All" table.
const ViewAllNameCell = ({ row }) => (
  <Typography variant="body2" sx={{ color: 'primary.main' }}>
    {row.original.name}
  </Typography>
);
ViewAllNameCell.propTypes = { row: viewAllRowShape.isRequired };

// Renders the template type in the Template column of the "View All" table.
const ViewAllTemplateCell = ({ row }) => (
  <Typography variant="body2" sx={{ color: 'primary.main' }}>
    {row.original.template}
  </Typography>
);
ViewAllTemplateCell.propTypes = { row: viewAllRowShape.isRequired };

// Renders the entity status as a Chip badge in the Status column of the "View All" table.
const ViewAllStatusCell = ({ row }) => (
  <Chip
    className="landscape-status-chip"
    label={row.original.status}
    size="small"
  />
);
ViewAllStatusCell.propTypes = { row: viewAllRowShape.isRequired };

// Renders the workflow label in the Workflow column of the "View All" table.
const ViewAllWorkflowCell = ({ row }) => (
  <Typography variant="body2">{row.original.workflow}</Typography>
);
ViewAllWorkflowCell.propTypes = { row: viewAllRowShape.isRequired };

// Renders the formatted creation date in the Created At column of the "View All" table.
const ViewAllCreatedAtCell = ({ row }) => (
  <Typography variant="body2">{row.original.createdAt}</Typography>
);
ViewAllCreatedAtCell.propTypes = { row: viewAllRowShape.isRequired };

const VIEW_ALL_COLUMNS = [
  {
    heading: 'Name',
    accessor: 'name',
    disableSortBy: false,
    Cell: ViewAllNameCell,
  },
  {
    heading: 'Template',
    accessor: 'template',
    disableSortBy: false,
    Cell: ViewAllTemplateCell,
  },
  {
    heading: 'Status',
    accessor: 'status',
    disableSortBy: true,
    Cell: ViewAllStatusCell,
  },
  {
    heading: 'Workflow',
    accessor: 'workflow',
    disableSortBy: false,
    Cell: ViewAllWorkflowCell,
  },
  {
    heading: 'Created At',
    accessor: 'createdAt',
    disableSortBy: false,
    Cell: ViewAllCreatedAtCell,
  },
];

// Returns default header cell props (left-aligned text) for every column in the "View All" table.
const getViewAllHeaderProps = () => ({ align: 'left' });

export const MultiEntityNode = memo((props) => {
  const { data } = props;
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [entityMenuWidth, setEntityMenuWidth] = useState(undefined);
  const entitySelectRef = React.useRef(null);
  const theme = useTheme();

  const { show: showContextMenu } = useContextMenu();
  const { zoom } = useViewport();
  const scrollBoxRef = useRef(null);

  const associated_entity = data?.associated_entities || {};
  const relation = data?.relation;

  const templates = useMemo(() => {
    return Object.keys(associated_entity);
  }, [associated_entity]);

  // Initialise template to alphabetically first on mount / when templates change
  useEffect(() => {
    if (templates.length > 0) {
      const sortedArr = [...templates].sort((a, b) => a.localeCompare(b));
      setSelectedTemplate(sortedArr[0]);
    }
  }, [templates]);

  // Sync selectedEntityId from controlled prop (upstream auto-selection from NewLandscape)
  useEffect(() => {
    if (data?.selectedEntityId !== undefined) {
      setSelectedEntityId(data.selectedEntityId || '');
    }
  }, [data?.selectedEntityId]);

  // All entities for the selected template (before parent filtering)
  const allTemplateArtifacts = useMemo(() => {
    return associated_entity?.[selectedTemplate] || [];
  }, [selectedTemplate, associated_entity]);

  // Entities filtered by upstream parent selection when filteredByParentEntityIds is set
  const selectedTemplateArtifacts = useMemo(() => {
    const filteredParentIds = data?.filteredByParentEntityIds;
    if (filteredParentIds?.length > 0) {
      return allTemplateArtifacts.filter((entity) =>
        entity.parent_id?.some((pid) => filteredParentIds.includes(pid))
      );
    }
    return allTemplateArtifacts;
  }, [allTemplateArtifacts, data?.filteredByParentEntityIds]);

  useEffect(() => {
    if (selectedEntityId || selectedTemplateArtifacts.length === 0) {
      return;
    }

    const firstEntity = selectedTemplateArtifacts[0];
    setSelectedEntityId(firstEntity?.id);
    data?.handleEntitySelect?.(
      props?.id,
      firstEntity?.id,
      firstEntity?.parent_id || []
    );
  }, [selectedEntityId, selectedTemplateArtifacts, data, props?.id]);

  // Total count used for "View All" – always from the full template list
  const totalCount = useMemo(
    () => allTemplateArtifacts.length,
    [allTemplateArtifacts]
  );

  // Placeholder icon/type should follow the active template's entity list.
  const firstEntityType = useMemo(() => {
    return selectedTemplateArtifacts[0]?.entity_type;
  }, [selectedTemplateArtifacts]);

  const firstEntityRequestType = useMemo(() => {
    return selectedTemplateArtifacts[0]?.request_type;
  }, [selectedTemplateArtifacts]);

  const clusterDisplayLabel = useMemo(() => {
    if (data?.level < 0) {
      return (
        getEntityTypeLabel(firstEntityType, firstEntityRequestType) || 'Entity'
      );
    }

    return relation?.name || 'Entities';
  }, [data?.level, firstEntityType, firstEntityRequestType, relation?.name]);

  // Dot color derived deterministically from the node id for visual distinctness
  const entityTypeDotColor = useMemo(
    () =>
      getColorFromId(props.id, theme?.palette?.landscape?.nodeDotColors || []),
    [props.id, theme]
  );

  const handleTemplateChange = useCallback(
    (event) => {
      const value = event?.target?.value;
      data?.handleSetTemplate?.({ [data.id]: value });
      setSelectedTemplate(value);
      setSelectedEntityId('');
      // Clear downstream filtering when template changes
      data?.handleEntitySelect?.(props.id, '', []);
    },
    [data, props.id, setSelectedTemplate, setSelectedEntityId]
  );

  // Updates the selected entity and propagates the selection to adjacent nodes via handleEntitySelect.
  const handleEntityChange = useCallback(
    (event) => {
      const entityId = event?.target?.value;
      const entity = selectedTemplateArtifacts.find((e) => e.id === entityId);
      setSelectedEntityId(entityId);
      data?.handleEntitySelect?.(props.id, entityId, entity?.parent_id || []);
    },
    [selectedTemplateArtifacts, data, props.id, setSelectedEntityId]
  );

  // Captures the dropdown trigger's current width so the entity menu can match it.
  const handleEntitySelectOpen = useCallback(() => {
    if (entitySelectRef.current) {
      setEntityMenuWidth(entitySelectRef.current.getBoundingClientRect().width);
    }
  }, [setEntityMenuWidth]);

  // Opens the "View All" dialog listing every entity in this cluster node.
  const handleViewAllOpen = useCallback(
    (e) => {
      e.stopPropagation();
      setViewAllOpen(true);
    },
    [setViewAllOpen]
  );

  // Closes the "View All" dialog.
  const handleViewAllClose = useCallback(
    () => setViewAllOpen(false),
    [setViewAllOpen]
  );

  // Transforms allTemplateArtifacts into flattened row objects for the "View All" table.
  const viewAllTableData = useMemo(
    () =>
      allTemplateArtifacts.map((entity) => {
        const entityProperties = entity?.data?.properties;
        return {
          _entity: entity,
          name: entityProperties?.unique_id || entity?.data?.label || '',
          template: entityProperties?.template_type || '',
          status: entityProperties?.status || '',
          workflow: entityProperties?.workflowLabel || '',
          createdAt: entityProperties?.created_at
            ? new Date(entityProperties.created_at).toLocaleString()
            : '',
        };
      }),
    [allTemplateArtifacts]
  );

  // Returns onClick cell props that open the clicked entity itself, matching the "Open Entity" action.
  const getViewAllCellProps = useCallback(
    (cell) => {
      return {
        onClick: (e) => {
          e.stopPropagation();
          const entity = cell?.row?.original?._entity;
          const entityProps = entity?.data?.properties;
          const resolvedEntityType =
            entityProps?.entity_type || entity?.entity_type;
          const resolvedRequestType =
            entityProps?.request_type || entity?.request_type;
          const resolvedUniqueId = entityProps?.unique_id;

          if (!resolvedEntityType || !resolvedUniqueId) {
            return;
          }

          const entityTypeKey = getEntityTypeKey(
            resolvedEntityType,
            resolvedRequestType
          );
          const entityTypeSegment = getLabelPathFromLabelConfig(
            DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || '',
            'path',
            DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
          );

          window.open(
            `/${entityTypeSegment}/${resolvedUniqueId}`,
            '_blank',
            'noopener,noreferrer'
          );
        },
        style: { cursor: 'pointer' },
      };
    },
    [getEntityTypeKey, getLabelPathFromLabelConfig]
  );

  // Renders the selected template name as a truncated tooltip text inside the template Select control.
  const renderTemplateSelectValue = useCallback(
    (value) => (
      <Tooltip title={value}>
        <Typography
          variant="body5"
          sx={{
            color: theme?.palette?.text?.secondary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {value}
        </Typography>
      </Tooltip>
    ),
    [theme]
  );

  // Stops click events from bubbling out of the "View All" dialog into the React Flow canvas.
  const handleDialogClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Triggers expansion of the selected entity's child nodes when the info icon is clicked.
  const handleInfoIconClick = useCallback(
    (e) => {
      e.stopPropagation();
      const entity =
        selectedTemplateArtifacts.find((ent) => ent.id === selectedEntityId) ||
        Object.values(associated_entity)
          .flat()
          .find((ent) => ent.id === selectedEntityId);
      if (entity) {
        data?.handleNodeEntityClick?.({
          hidden: false,
          cluster_node: { ...entity, level: data?.level, node_id: props.id },
        });
      }
    },
    [
      selectedEntityId,
      selectedTemplateArtifacts,
      associated_entity,
      data,
      props.id,
    ]
  );

  // Renders the entity Select's display value: a placeholder with type icon when empty, or the selected entity's id with an info-icon tooltip.
  const renderEntitySelectValue = useCallback(
    (value) => {
      if (!value) {
        const placeholderText = `Select ${clusterDisplayLabel}`;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              overflow: 'hidden',
              minWidth: 0,
            }}
          >
            {firstEntityType && (
              <EntityIcon
                entity_type={firstEntityType}
                request_type={firstEntityRequestType}
                size={13}
                wrapperSize={20}
              />
            )}
            <Tooltip title={placeholderText} placement="top">
              <Typography
                variant="body3"
                sx={{
                  color: theme?.palette?.text?.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {placeholderText}
              </Typography>
            </Tooltip>
          </Box>
        );
      }
      const entity =
        selectedTemplateArtifacts.find((e) => e.id === value) ||
        Object.values(associated_entity)
          .flat()
          .find((e) => e.id === value);
      const uniqueId = entity
        ? entity?.data?.properties?.unique_id || entity?.data?.label
        : value;
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            overflow: 'hidden',
          }}
        >
          {entity && (
            <EntityIcon
              entity_type={entity?.entity_type}
              request_type={entity?.request_type || entity?.type}
              size={13}
              wrapperSize={20}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              overflow: 'hidden',
              flex: 1,
            }}
          >
            <Tooltip title={uniqueId || ''}>
              <Typography
                variant="body3"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {uniqueId}
              </Typography>
            </Tooltip>
            <Tooltip
              slotProps={{
                tooltip: {
                  sx: { p: 0 },
                },
              }}
              title={
                entity ? (
                  <TitleComponent
                    data={entity?.data?.properties}
                    theme={theme}
                    entityName={
                      entity?.data?.label || entity?.data?.properties?.unique_id
                    }
                  />
                ) : (
                  ''
                )
              }
            >
              <InfoOutlinedIcon
                onClick={handleInfoIconClick}
                sx={{
                  fontSize: '16px',
                  color: theme?.palette?.text?.secondary,
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          </Box>
        </Box>
      );
    },
    [
      clusterDisplayLabel,
      relation?.name,
      firstEntityType,
      firstEntityRequestType,
      selectedTemplateArtifacts,
      associated_entity,
      theme,
      handleInfoIconClick,
    ]
  );

  return (
    <Card
      className="landscape-cluster-node"
      sx={{
        position: 'relative',
        borderColor: theme?.palette?.entityOverview?.dividerBorder,
        backgroundColor: theme?.palette?.landscape?.bg?.main,
      }}
    >
      {/* Header row: colored dot + relation name (left) + template label or dropdown (right) */}
      <Box
        ref={scrollBoxRef}
        className="nodrag nopan"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Tooltip
            slotProps={{
              tooltip: {
                sx: { p: 0 },
              },
            }}
            title={
              <Card className="landscape-relation-tooltip">
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  <Typography
                    variant="body5"
                    sx={{ color: theme?.palette?.landscape?.tooltipLabelColor }}
                  >
                    Relation Type
                  </Typography>
                  <Typography
                    variant="body4"
                    sx={{ color: theme?.palette?.grey?.T80 }}
                  >
                    {relation?.is_bidirectional
                      ? 'Bi-Directional'
                      : 'Uni-Directional'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  <Typography
                    variant="body5"
                    sx={{ color: theme?.palette?.landscape?.tooltipLabelColor }}
                  >
                    Cardinality
                  </Typography>
                  <Typography
                    variant="body4"
                    sx={{ color: theme?.palette?.grey?.T80 }}
                  >
                    {relation?.cardinality || '—'}
                  </Typography>
                </Box>
              </Card>
            }
          >
            <DotIndicator
              sx={{
                backgroundColor: entityTypeDotColor,
              }}
            >
              <Box
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: theme?.palette?.landscape?.bg?.main,
                }}
              />
            </DotIndicator>
          </Tooltip>
          <Tooltip title={clusterDisplayLabel}>
            <Typography
              variant="body5"
              sx={{
                color: theme?.palette?.text?.secondary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {clusterDisplayLabel}
            </Typography>
          </Tooltip>
        </Box>

        {/* Show dropdown only when multiple templates exist, otherwise plain label */}
        {templates.length > 1 ? (
          <Select
            className="nodrag nopan landscape-template-select"
            value={selectedTemplate}
            onChange={handleTemplateChange}
            size="small"
            variant="standard"
            IconComponent={ExpandMoreIcon}
            renderValue={renderTemplateSelectValue}
            sx={{
              color: theme?.palette?.text?.secondary,
              '& .MuiSelect-select': {
                color: `${theme?.palette?.text?.secondary} !important`,
              },
              '& .MuiSelect-icon': {
                color: `${theme?.palette?.text?.secondary} !important`,
              },
            }}
            MenuProps={{
              disablePortal: false,
              PaperProps: {
                style: { width: '117px', minWidth: '117px', maxWidth: '117px' },
              },
            }}
          >
            {templates.map((t) => (
              <MenuItem key={t} value={t}>
                <Tooltip title={t}>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      lineHeight: '22px',
                      letterSpacing: 0,
                      color: theme?.palette?.text?.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t}
                  </Typography>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Tooltip title={selectedTemplate}>
            <Typography
              variant="body5"
              sx={{
                color: theme?.palette?.text?.secondary,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {selectedTemplate}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {/* Entity select dropdown with linked_entity_count */}
      <Select
        ref={entitySelectRef}
        className="nodrag nopan landscape-entity-select"
        value={selectedEntityId}
        onChange={handleEntityChange}
        onOpen={handleEntitySelectOpen}
        size="small"
        displayEmpty
        renderValue={renderEntitySelectValue}
        IconComponent={ExpandMoreIcon}
        MenuProps={{
          disablePortal: false,
          PaperProps: {
            style: entityMenuWidth
              ? {
                  minWidth: entityMenuWidth,
                  maxWidth: entityMenuWidth,
                  maxHeight: '40vh',
                  overflowY: 'auto',
                }
              : { maxHeight: '40vh', overflowY: 'auto' },
            sx: {
              p: `${8 * zoom}px`,
              marginTop: `${4 * zoom}px`,
              border: `1px solid ${theme?.palette?.other?.borderColor}`,
              borderRadius: `${6 * zoom}px`,
              '& .MuiList-root': {
                p: 0,
              },
              '& .MuiMenuItem-root.Mui-selected': {
                backgroundColor: theme?.palette?.landscape?.entityMenuItemHover,
                color: theme?.palette?.text?.primary,
              },
              '& .MuiMenuItem-root.Mui-selected:hover': {
                backgroundColor: theme?.palette?.landscape?.entityMenuItemHover,
              },
              '& .MuiMenuItem-root:hover': {
                backgroundColor: theme?.palette?.landscape?.entityMenuItemHover,
              },
            },
          },
        }}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme?.palette?.other?.borderColor,
            borderRadius: '8px',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: `${theme?.palette?.other?.borderColor} !important`,
            borderWidth: '1px !important',
          },
          '&.Mui-focused': {
            borderColor: `${theme?.palette?.other?.borderColor} !important`,
            borderWidth: '1px !important',
          },
          '& .MuiSelect-icon': {
            color: `${theme?.palette?.text?.secondary} !important`,
          },
        }}
      >
        {selectedTemplateArtifacts.map((entity) => {
          const uniqueId =
            entity?.data?.properties?.unique_id || entity?.data?.label;
          const count = entity?.data?.properties?.linked_entities;
          return (
            <EntityMenuItem
              key={entity.id}
              value={entity.id}
              entity={entity}
              uniqueId={uniqueId}
              count={count}
              showContextMenu={showContextMenu}
              theme={theme}
              zoom={zoom}
            />
          );
        })}
      </Select>

      {/* View All link */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Typography
          className="nodrag nopan"
          variant="captionSemibold"
          sx={{
            color: theme?.palette?.primary?.main,
            cursor: 'pointer',
            letterSpacing: '0.03px',
          }}
          onClick={handleViewAllOpen}
        >
          {`View All  (${totalCount})`}
        </Typography>
      </Box>

      <Handle
        type="source"
        position={Position.Right}
        id="source-uni"
        isConnectable={true}
        style={{
          top: '30%',
          opacity: 0,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="source-bi"
        isConnectable={true}
        style={{
          top: '70%',
          opacity: 0,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="b"
        isConnectable={true}
        style={{
          top: '50%',
          opacity: 0,
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="target-uni"
        isConnectable={true}
        style={{
          top: '30%',
          opacity: 0,
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="target-bi"
        isConnectable={true}
        style={{
          top: '70%',
          opacity: 0,
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="b"
        isConnectable={true}
        style={{
          top: '50%',
          opacity: 0,
        }}
      />

      {/* View All Dialog – rendered via Portal, outside React Flow canvas */}
      {viewAllOpen && (
        <Dialog
          open={viewAllOpen}
          onClose={handleViewAllClose}
          maxWidth="md"
          fullWidth
          onClick={handleDialogClick}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: '24px',
              py: '16px',
            }}
          >
            <Typography variant="subtitle3">
              {'All ' + (clusterDisplayLabel || 'Entities')}
            </Typography>
            <IconButton onClick={handleViewAllClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ px: '24px', py: '16px' }}>
            <ReactMuiTableListViewV8
              columns={VIEW_ALL_COLUMNS}
              data={viewAllTableData}
              getCellProps={getViewAllCellProps}
              getHeaderProps={getViewAllHeaderProps}
              enablePagination
              showPaginationAction
            />
          </DialogContent>
          <DialogActions
            sx={{ px: '24px', pb: '16px', justifyContent: 'flex-end' }}
          >
            <SolyticsButton variant="contained" onClick={handleViewAllClose}>
              Close
            </SolyticsButton>
          </DialogActions>
        </Dialog>
      )}
    </Card>
  );
});
MultiEntityNode.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.instanceOf(Object).isRequired,
};

// info: not completed from backend side yet
export const AttributeNode = memo((props) => {
  const { data } = props;
  const theme = useTheme();
  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: theme?.palette?.primary?.main,
        borderRadius: '8px',
        width: '150px',
      }}
    >
      <List
        sx={{
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: `${theme?.palette?.landscape?.bg?.main} !important`,
        }}
      >
        {Object.keys(data?.properties || {}).map((key) => {
          const label = data?.properties[key]?.label;
          return (
            <ListItemButton
              button
              key={key}
              sx={{
                display: 'flex',
                width: '100%',
                overflow: 'hidden',
                padding: '4px',
                gap: '8px',
                borderRadius: '4px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '100%',
                  backgroundColor: theme?.palette?.landscape?.bg?.attribute,
                  width: '30px',
                  height: '30px',
                }}
              >
                <AttributeIcon
                  style={{
                    width: '20px',
                    height: '20px',
                    color: theme?.palette?.landscape?.attribute,
                  }}
                />
              </Box>
              <Typography
                variant="body3"
                sx={{
                  color: theme?.palette?.primary?.main,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {upperCase(label)}
              </Typography>
            </ListItemButton>
          );
        })}
      </List>
      <Handle
        type="source"
        position={Position.Right}
        id="b"
        isConnectable={true}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="b"
        isConnectable={true}
      />
    </Card>
  );
});
AttributeNode.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
};

export const LineageCardNode = memo((props) => {
  const data = props?.data?.properties;
  const theme = useTheme();
  const entityName = props?.data?.entity_alias || props?.data?.label;
  const entityId = data?.unique_id || props?.data?.entity_id;

  const { show: showContextMenu } = useContextMenu();
  const handleContextMenu = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      showContextMenu({
        id: 'node-rightClick',
        event: e,
        props,
      });
    },
    [props, showContextMenu]
  );

  return (
    <Tooltip
      slotProps={{
        tooltip: {
          sx: {
            p: 0,
          },
        },
      }}
      title={
        <TitleComponent data={data} theme={theme} entityName={entityName} />
      }
    >
      <Card
        onContextMenu={handleContextMenu}
        className="landscape-lineage-node"
        sx={{
          position: 'relative',
          borderColor: theme?.palette?.primary?.main,
          ...(props?.type === 'main_entity_node'
            ? { backgroundColor: theme?.palette?.landscape?.mainNode }
            : { backgroundColor: theme?.palette?.landscape?.bg?.main }),
        }}
      >
        <Typography
          variant="body3"
          sx={{
            color: theme?.palette?.primary?.main,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {entityName}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <EntityIcon
            entity_type={props?.data?.entity_type || data?.entity_type}
            request_type={props?.data?.request_type || data?.request_type}
          />
          <Typography
            variant="body3"
            sx={{
              color: theme?.palette?.secondary?.light,
            }}
          >
            {entityId || 'No ID'}
          </Typography>
        </Box>
        <Handle
          type="source"
          position={Position.Right}
          id="source-uni"
          style={{
            top: '30%',
            bottom: 'auto',
            background: theme?.palette?.landscape?.lineageHandleColor,
          }}
          isConnectable={true}
        />
        <Handle
          type="source"
          position={Position.Right}
          id="source-bi"
          style={{
            top: '70%',
            bottom: 'auto',
            background: theme?.palette?.landscape?.lineageHandleColor,
          }}
          isConnectable={true}
        />
        <Handle
          type="target"
          position={Position.Left}
          id="target-left"
          style={{
            top: '50%',
            bottom: 'auto',
            background: theme?.palette?.landscape?.lineageHandleColor,
          }}
          isConnectable={true}
        />
      </Card>
    </Tooltip>
  );
});
LineageCardNode.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  type: PropTypes.string,
};
